import { Injectable } from '@angular/core';
import { db } from '../db/db';
import { StorageRecord } from '../models/storage-record';
import { StorageTTLConfig } from '../config/storage-ttl-config';
import { requestIdleOrTimeout } from '../utils/idle-helper';

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private cleanupExecuted: boolean = false;
  private dbReadyPromise: Promise<void> | null = null;

  constructor() {
    // Cleanup is scheduled explicitly to avoid blocking app bootstrap.
  }

  /**
   * Initialize cleanup process - runs once when service is instantiated
   */
  private async initCleanup() {
    if (!this.cleanupExecuted) {
      try {
        await this.ensureDatabaseReady();
        await this.deleteExpiredRecords();
        this.cleanupExecuted = true;
        // console.log('Storage service initialized with expired records cleanup');
      } catch (error) {
        console.warn('Storage cleanup failed:', error);
      }
    }
  }

  /**
   * Schedule cleanup work after the app becomes idle.
   */
  scheduleCleanupIdle(timeout: number = 2000): void {
    if (this.cleanupExecuted) {
      return;
    }
    requestIdleOrTimeout(() => {
      void this.initCleanup();
    }, timeout);
  }

  /**
   * Ensures the Dexie database is opened before performing IndexedDB operations.
   */
  private async ensureDatabaseReady(timeout: number = 3000): Promise<void> {
    if (db.isOpen()) return;

    if (!this.dbReadyPromise) {
      this.dbReadyPromise = new Promise<void>(async (resolve, reject) => {
        const timer = setTimeout(() => {
          this.dbReadyPromise = null;
          reject(new Error('IndexedDB opening timed out'));
        }, timeout);
        try {
          await db.ensureInitialized();
          clearTimeout(timer);
          resolve();
        } catch (error) {
          clearTimeout(timer);
          reject(error);
        } finally {
          this.dbReadyPromise = null;
        }
      });
    }

    return this.dbReadyPromise.catch((error) => {
      console.warn('Failed to open IndexedDB:', error);
      throw error;
    });
  }

  /**
   * Get an item from IndexedDB storage
   * @param key - The key to retrieve
   * @returns Promise that resolves to the value or null if not found/expired
   */
  async getItem<T>(key: string): Promise<T | null> {
    try {
      if (!db.isOpen()) {
        try {
          await this.ensureDatabaseReady();
        } catch (error) {
          console.warn('Database is not open, falling back to localStorage');
          try {
            const fallbackValue = localStorage.getItem(key);
            return fallbackValue ? JSON.parse(fallbackValue) : null;
          } catch (fallbackError) {
            return null;
          }
        }
      }

      const record = await db.storageRecords.where('key').equals(key).first();
      
      if (!record) {
        return null;
      }

      // Check if record is expired
      if (record.expiresAt && record.expiresAt <= Date.now()) {
        // Delete expired record
        await this.removeItem(key);
        return null;
      }

      try {
        return JSON.parse(record.value) as T;
      } catch (parseError) {
        console.warn(`Failed to parse stored value for key "${key}":`, parseError);
        return record.value as T;
      }
    } catch (error) {
      console.error(`Error getting item "${key}" from storage:`, error);
      // Fallback to localStorage if IndexedDB fails
      try {
        const fallbackValue = localStorage.getItem(key);
        return fallbackValue ? JSON.parse(fallbackValue) : null;
      } catch (fallbackError) {
        return null;
      }
    }
  }

  /**
   * Set an item in IndexedDB storage
   * @param key - The key to store the value under
   * @param value - The value to store
   * @param ttlInMs - Optional time-to-live in milliseconds
   */
  async setItem<T>(key: string, value: T, ttlInMs?: number): Promise<void> {
    try {
      if (!db.isOpen()) {
        try {
          await this.ensureDatabaseReady();
        } catch (error) {
          console.warn('Database is not open, using localStorage fallback');
          localStorage.setItem(key, JSON.stringify(value));
          return;
        }
      }

      const now = Date.now();
      const record: StorageRecord = {
        key,
        value: typeof value === 'string' ? value : JSON.stringify(value),
        createdAt: now,
        expiresAt: ttlInMs ? StorageTTLConfig.createExpirationTimestamp(ttlInMs) : undefined
      };

      // Use a transaction to ensure atomic operation
      await db.transaction('rw', db.storageRecords, async () => {
        // Check if record exists first to handle potential duplicates
        const existing = await db.storageRecords.where('key').equals(key).first();
        
        if (existing) {
          // Update existing record
          record.id = existing.id;
          await db.storageRecords.put(record);
        } else {
          // Add new record
          await db.storageRecords.add(record);
        }
      });
      
    } catch (error) {
      console.error(`Error setting item "${key}" in storage:`, error);
      
      // Handle constraint errors specifically
      if (error.name === 'ConstraintError') {
        // Try to update instead of add
        try {
          const existing = await db.storageRecords.where('key').equals(key).first();
          if (existing) {
            const updatedRecord = {
              ...existing,
              value: typeof value === 'string' ? value : JSON.stringify(value),
              createdAt: Date.now(),
              expiresAt: ttlInMs ? StorageTTLConfig.createExpirationTimestamp(ttlInMs) : undefined
            };
            await db.storageRecords.put(updatedRecord);
            return;
          }
        } catch (retryError) {
          console.error('Retry after constraint error failed:', retryError);
        }
      }
      
      // Fallback to localStorage if IndexedDB fails
      try {
        localStorage.setItem(key, JSON.stringify(value));
      } catch (fallbackError) {
        console.error('Both IndexedDB and localStorage failed:', fallbackError);
        throw error;
      }
    }
  }

  /**
   * Remove an item from IndexedDB storage
   * @param key - The key to remove
   */
  async removeItem(key: string): Promise<void> {
    try {
      if (!db.isOpen()) {
        console.warn('Database is not open, using localStorage fallback');
        localStorage.removeItem(key);
        return;
      }

      await db.storageRecords.where('key').equals(key).delete();
    } catch (error) {
      console.error(`Error removing item "${key}" from storage:`, error);
      // Fallback to localStorage if IndexedDB fails
      localStorage.removeItem(key);
    }
  }

  /**
   * Clear all items from IndexedDB storage
   */
  async clear(): Promise<void> {
    try {
      if (!db.isOpen()) {
        console.warn('Database is not open, using localStorage fallback');
        localStorage.clear();
        return;
      }

      await db.storageRecords.clear();
    } catch (error) {
      console.error('Error clearing storage:', error);
      // Fallback to localStorage if IndexedDB fails
      localStorage.clear();
    }
  }

  /**
   * Delete all expired records from IndexedDB storage
   * Uses index for efficient deletion without reading full records
   */
  async deleteExpiredRecords(): Promise<void> {
    try {
      if (!db.isOpen()) {
        try {
          await this.ensureDatabaseReady();
        } catch (error) {
          console.warn('Database is not open, cannot clean expired records');
          return;
        }
      }

      const now = Date.now();
      
      // Use the expiresAt index to efficiently find and delete expired records
      const deleteCount = await db.storageRecords
        .where('expiresAt')
        .below(now)
        .delete();

      if (deleteCount > 0) {
        // console.log(`Cleaned up ${deleteCount} expired storage records`);
      }
    } catch (error) {
      console.error('Error cleaning up expired records:', error);
    }
  }

  /**
   * Get all keys from storage (for debugging/migration purposes)
   */
  async getAllKeys(): Promise<string[]> {
    try {
      if (!db.isOpen()) {
        try {
          await this.ensureDatabaseReady();
        } catch (error) {
          return Object.keys(localStorage);
        }
      }

      const records = await db.storageRecords.orderBy('key').keys();
      return records.map(record => String(record));
    } catch (error) {
      console.error('Error getting all keys:', error);
      return [];
    }
  }

  /**
   * Migrate data from localStorage to IndexedDB
   * This method should be called once during app initialization
   */
  async migrateFromLocalStorage(): Promise<void> {
    try {
      if (!db.isOpen()) {
        try {
          await this.ensureDatabaseReady();
        } catch (error) {
          console.warn('Database is not open, cannot migrate from localStorage');
          return;
        }
      }

      const localStorageKeys = Object.keys(localStorage);
      let migratedCount = 0;

      for (const key of localStorageKeys) {
        try {
          // Check if key already exists in IndexedDB
          const existing = await db.storageRecords.where('key').equals(key).first();
          if (existing) {
            continue; // Skip if already migrated
          }

          const value = localStorage.getItem(key);
          if (value !== null) {
            await this.setItem(key, value);
            migratedCount++;
          }
        } catch (error) {
          console.warn(`Failed to migrate key "${key}":`, error);
        }
      }

      if (migratedCount > 0) {
        // console.log(`Migrated ${migratedCount} items from localStorage to IndexedDB`);
      }
    } catch (error) {
      console.error('Error during localStorage migration:', error);
    }
  }

  /**
   * Check if IndexedDB is available and working
   */
  async isAvailable(): Promise<boolean> {
    try {
      if (db.isOpen()) return true;
      await this.ensureDatabaseReady();
      return db.isOpen();
    } catch (error) {
      return false;
    }
  }

  /**
   * Get storage statistics for debugging
   */
  async getStorageStats(): Promise<{total: number, expired: number, validKeys: string[]}> {
    try {
      if (!db.isOpen()) {
        try {
          await this.ensureDatabaseReady();
        } catch (error) {
          return {
            total: Object.keys(localStorage).length,
            expired: 0,
            validKeys: Object.keys(localStorage)
          };
        }
      }

      const allRecords = await db.storageRecords.toArray();
      const now = Date.now();
      const validRecords = allRecords.filter(record => !record.expiresAt || record.expiresAt > now);
      const expiredRecords = allRecords.filter(record => record.expiresAt && record.expiresAt <= now);

      return {
        total: allRecords.length,
        expired: expiredRecords.length,
        validKeys: validRecords.map(r => r.key)
      };
    } catch (error) {
      console.error('Error getting storage stats:', error);
      return { total: 0, expired: 0, validKeys: [] };
    }
  }
}

